
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MdDeleteForever } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import '../../style/ManageFoundPetImagesPage.css';


const ManageFoundPetImagesPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [foundPet, setFoundPet] = useState(null);
    const [newImages, setNewImages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

    useEffect(() => {
        const fetchFoundPet = async () => {
            try {
                const storedUser = JSON.parse(localStorage.getItem('user'));
                const token = storedUser?.token;
                if (!token) throw new Error(t('error.noAuthToken'));
                const response = await axios.get(`${baseUrl}/api/found-pets/${id}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setFoundPet(response.data);
            } catch (err) {
                setError(err.message || t('error.fetchPetDetails'));
            }
        };
        fetchFoundPet();
    }, [id, baseUrl, t]);

    const handleImageUpload = async () => {
        try {
            setLoading(true);
            const storedUser = JSON.parse(localStorage.getItem('user'));
            const token = storedUser?.token;
            if (!token) throw new Error(t('error.noAuthToken'));
            const formData = new FormData();
            newImages.forEach((image) => {
                formData.append('images', image);
            });
            const response = await axios.put(`${baseUrl}/api/found-pets/${id}/images`, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
            });
            setFoundPet(response.data);
            setNewImages([]);
            setLoading(false);
            navigate(`/found-pet/${id}`);
        } catch (err) {
            setError(err.message || t('error.uploadImages'));
            setLoading(false);
        }
    };

    const handleImageDelete = async (imageUrl) => {
        try {
            const storedUser = JSON.parse(localStorage.getItem('user'));
            const token = storedUser?.token;
            if (!token) throw new Error(t('error.noAuthToken'));
            const response = await axios.delete(`${baseUrl}/api/found-pets/${id}/images`, {
                headers: { Authorization: `Bearer ${token}` },
                data: { imageUrl },
            });
            setFoundPet(response.data);
        } catch (err) {
            setError(err.message || t('error.deleteImage'));
        }
    };

    const handleImageChange = (e) => {
        const files = Array.from(e.target.files);
        setNewImages(files);
    };

    const renderImagePreviews = () => {
        return newImages.map((image, index) => (
            <div key={index} className="image-preview-container">
                <img
                    src={URL.createObjectURL(image)}
                    alt={t('manageFoundPetImagesPage.previewAlt', { index: index + 1 })}
                />
            </div>
        ));
    };

    if (error) {
        return <div>{t('error.generalError', { error })}</div>;
    }
    if (!foundPet) {
        return <div>{t('loading')}</div>;
    }

    // Show all images for found pets (or filter/tag if needed)
    const foundImages = foundPet.images && Array.isArray(foundPet.images)
        ? foundPet.images.map(img => (typeof img === 'string' ? { url: img } : img))
        : [];

    return (
        <div className="manage-pet-images-page">
            <div className="manage-pet-images-header">
                <h1>{t('manageFoundPetImagesPage.title')}</h1>
            </div>
            <div className="pet-images">
                {foundImages.map((image, index) => (
                    <div key={index} className="image-container">
                        <img
                            src={image.url}
                            alt={t('manageFoundPetImagesPage.imageAlt', { index: index + 1 })}
                        />
                        <button onClick={() => handleImageDelete(image.url)} className="delete-icon">
                            <MdDeleteForever size={24} />
                        </button>
                    </div>
                ))}
            </div>
            <div className="upload-images">
                <h3>{t('manageFoundPetImagesPage.uploadTitle')}</h3>
                <input type="file" accept="image/*" multiple onChange={handleImageChange} />
                <div className="image-previews">{renderImagePreviews()}</div>
                <button onClick={handleImageUpload} disabled={loading || newImages.length === 0}>
                    {loading ? t('manageFoundPetImagesPage.uploading') : t('manageFoundPetImagesPage.uploadButton')}
                </button>
            </div>
        </div>
    );
};

export default ManageFoundPetImagesPage;